import { ChatCommentInterface, CommentInterface, MessageCommentInterface } from '@/services/QA/comment/interfaces';
import { ChatNoteInterface, NoteInterface } from '@/services/QA/note/interfaces';
import { TagInterface } from '@/services/QA/tag/interfaces';
import { ScoreTypeInterface } from '@/services/QA/scoreType/interfaces';
import { ScoreGradeInterface } from '@/services/QA/scoreGrade/interfaces';

export interface ChatEvent {
  id: string,
  text: string,
  timestamp: number,
  type: string,
  usertype: string,
}

export interface AgentInterface {
  id?: string,
  fullName?: string,
  name: string,
  login: string,
}

export interface VisitorInterface {
  id: string,
  hbId?: number,
  name: string,
  email: string,
  isLogged: boolean
}

export interface AttachmentFilesInterface {
  url: string,
  contentType: string,
}

export enum MessageRecipientsType {
  ALL = 'all',
  AGENTS = 'agents',
}

export interface ChatMessageInterface {
  id: number,
  authorName: string,
  userType: string,
  recipients: MessageRecipientsType
  text: string,
  timestamp: number
  isAttachment: boolean;
  isWhisper: boolean;
  agentId: string;
  attachmentFiles?: AttachmentFilesInterface[]
  commentInputVisible?: boolean
  comments: MessageCommentInterface[]
}

export interface ScoreInterface {
  id: number;
  type: ScoreTypeInterface
  grade: ScoreGradeInterface
  authorId: string,
  deletedAt: string,
}

export interface ChatScoreInterface {
  chatId: string,
  score: ScoreInterface,
}

export interface ChatInterface {
  id: string;
  startedTimestamp: number;
  endedTimestamp: number;
  reviewerId: string,
  reviewedOn?: number,
  rate: string;
  messages: ChatMessageInterface[]
  visitor: VisitorInterface,
  agents: AgentInterface[],
  supervisors: AgentInterface[],
  comments: ChatCommentInterface[],
  notes: ChatNoteInterface[],
  tags: TagInterface[],
  scores: ChatScoreInterface[],
  forLater: boolean,
}

export interface ChatsWSMessageInterface {
  chats: ChatInterface[];
  totalCount: number;
}

export interface ChatsSearchResultInterface {
  chats: ChatInterface[],
  totalItems: number,
}

export interface ChatWSMessageInterface {
  chat: ChatInterface;
}

export interface ChatScoreRequestInterface {
  [scoreTypeId: number]: number;
}

export const chatRateMap = {
  0: 'Not Rated',
  1: 'Rated Bad',
  2: 'Rated Good',
};

export default interface ChatRepositoryInterface {
  getLastUnreviewedChat(chatId?: string): Promise<ChatInterface>;

  getAllChats(): Promise<Array<Partial<ChatInterface>>>;

  getOneById(chatId: string): Promise<ChatInterface>;

  getAllReviewedChats(
    page: number,
    sortBy: string,
    sortDesc: boolean,
  ): Promise<[Array<Partial<ChatInterface>>, number]>;

  getAllUnreviewedChats(
    page: number,
    sortBy: string,
    sortDesc: boolean,
  ): Promise<[Array<Partial<ChatInterface>>, number]>;

  getUnfinishedReviews(): Promise<Partial<ChatInterface>>;

  syncChats(): Promise<void>;

  clearChatQueue(timestamp: number, isDryRun: boolean): Promise<number>;

  reviewChat(
    chatId: string,
    agents: string[],
    scores: ChatScoreRequestInterface,
    tags: number[],
    sendStats?: boolean,
    reviewLater?: boolean,
  ): Promise<void>;

  searchChats(currentPage: number, search: string, sortBy: string, sortDesc: boolean): Promise<void>;

  getAgentChatIds(
    email: string,
    page: number,
    sortBy: string,
    sortDesc: boolean,
    reviewedIds?: string[],
  ): Promise<[string[], number]>;

  getAgentUnreviewedChats(chatIds: string[], page: number): Promise<[Array<Partial<ChatInterface>>, number]>;
}
